import React, { useState } from 'react';
import {
  Box,
  Text,
  Heading,
  Button,
  TextArea,
  Divider,
} from '@razorpay/blade/components';
import { DecisionNode } from '../../types';
import { DECISION_PROMPT_SUGGESTIONS } from '../../constants';
import { DecisionConfigurationProps } from './types';

const DecisionConfiguration = ({
  decision,
  onConfigChange,
  onSave,
}: DecisionConfigurationProps) => {
  const [prompt, setPrompt] = useState(decision?.prompt || '');

  const handlePromptChange = (value: string) => {
    setPrompt(value);
    onConfigChange({
      id: decision?.id || 'decision-1',
      prompt: value,
      isEnabled: true,
    });
  };

  const handleSave = () => {
    const updatedDecision: DecisionNode = {
      id: decision?.id || 'decision-1',
      prompt,
      isEnabled: true,
    };
    onSave(updatedDecision);
  };

  return (
    <Box display="flex" flexDirection="column" gap="spacing.5">
      <Box display="flex" alignItems="center" gap="spacing.3">
        <Text size="large">🤖</Text>
        <Heading size="medium">Decide</Heading>
      </Box>

      <Text color="surface.text.gray.muted">
        Let AI decide the action to take based on the prompt
      </Text>

      <Divider />

      <Box display="flex" flexDirection="column" gap="spacing.3" padding="1px">
        <Text weight="semibold">Enter a prompt*</Text>
        <TextArea
          label=""
          placeholder="Describe what the AI should evaluate..."
          value={prompt}
          onChange={({ value }) => handlePromptChange(value || '')}
          numberOfLines={4}
        />
        <Text size="small" color="surface.text.gray.muted">
          AI can make mistakes, so it's important to review the decision before saving.{' '}
        </Text>
      </Box>

      <Box display="flex" flexDirection="column" gap="spacing.2">
        <Text size="small" color="surface.text.gray.muted">
          Suggestions:
        </Text>
        <Box display="flex" flexWrap="wrap" gap="spacing.2">
          {DECISION_PROMPT_SUGGESTIONS.map((suggestion, index) => (
            <Button
              key={index}
              variant="tertiary"
              size="small"
              onClick={() => handlePromptChange(suggestion)}>
              ✨ {suggestion}
            </Button>
          ))}
        </Box>
      </Box>

      <Box
        padding="spacing.4"
        backgroundColor="surface.background.gray.moderate"
        borderRadius="medium">
        <Text size="small" weight="semibold" marginBottom="spacing.2">
          How Decide works
        </Text>
        <Text size="small" color="surface.text.gray.muted">
          After Decide, set which actions to take based on the result. 
          For example, approve a request only if it's valid. If AI doesn't have enough 
          information to decide, it defaults to false and sends for manual review.
        </Text>
      </Box>

      <Box marginTop="spacing.4">
        <Button onClick={handleSave} isFullWidth isDisabled={!prompt.trim()}>
          Save Decision
        </Button>
      </Box>
    </Box>
  );
};

export { DecisionConfiguration };
